// src/pages/admin/opex/OpexInvoiceList.jsx
import { useState, useEffect } from 'react';
import { Box, Typography, Snackbar, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router';
import MainCard from 'components/MainCard';
import StatisticsCards from 'components/StatisticsCards';
import { DataTable, TableFilters } from 'components/DataTable';
import ConfirmDialog from 'components/ConfirmDialog';
import opexInvoiceService from 'services/opexInvoiceService';
import { formatCurrency } from 'utils/formatters';
import { getInvoiceStatusColor, getStatusLabel, getInvoiceStatusLabel } from 'utils/statusHelpers';
import { useSnackbar } from 'hooks/useSnackbar';
import { useAsync } from 'hooks/useAsync';

const OpexInvoiceList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [categories, setCategories] = useState({});

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const [confirmDialog, setConfirmDialog] = useState({ open: false, invoiceId: null });
  const { showSuccess, showError, snackbar, handleClose } = useSnackbar();

  const { loading, execute: fetchInvoices } = useAsync(async () => {
    // Fetch all invoices without filters - filtering happens on frontend
    const response = await opexInvoiceService.getInvoices();
    setInvoices(response.data || []);
  });

  useEffect(() => {
    fetchInvoices();
    fetchStatistics();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatistics = async () => {
    try {
      const stats = await opexInvoiceService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await opexInvoiceService.getCategories();
      // Backend returns {data: categories}, so extract the data
      const categoriesData = response.data || response;
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      await opexInvoiceService.markAsPaid(confirmDialog.invoiceId, 'Marked as Remittance Advice from OPEX invoice list.');
      fetchInvoices();
      fetchStatistics();
      setConfirmDialog({ open: false, invoiceId: null });
      showSuccess('Invoice marked as paid successfully');
    } catch (error) {
      console.error('Failed to mark invoice as paid:', error);
      showError('Failed to mark invoice as paid');
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  // Client-side filtering
  const filteredInvoices = invoices.filter((invoice) => {
    // Exclude draft invoices for admin view
    if (invoice.status === 'draft') {
      return false;
    }
    const invoiceNumber = invoice.custom_invoice_number || invoice.invoice_number;
    // Search filter (invoice number, description, vendor name)
    if (
      search &&
      !invoiceNumber.toLowerCase().includes(search.toLowerCase()) &&
      (!invoice.description || !invoice.description.toLowerCase().includes(search.toLowerCase())) &&
      (!invoice.vendor?.company_name || !invoice.vendor.company_name.toLowerCase().includes(search.toLowerCase()))
    ) {
      return false;
    }
    // Status filter
    if (statusFilter !== 'all' && invoice.status !== statusFilter) {
      return false;
    }
    // Category filter
    if (categoryFilter !== 'all' && invoice.opex_category !== categoryFilter) {
      return false;
    }
    return true;
  });

  const statisticsData = statistics
    ? [
        {
          label: 'Total',
          value: statistics.total,
          subtitle: formatCurrency(statistics.total_amount, statistics.currency)
        },
        { label: 'Submitted', value: statistics.submitted, color: 'warning.main' },
        { label: 'Approved', value: statistics.approved, color: 'primary.main' },
        {
          label: 'Remittance Advice',
          value: statistics.paid,
          subtitle: formatCurrency(statistics.total_paid_amount, statistics.currency),
          color: 'success.main'
        },
        { label: 'Rejected', value: statistics.rejected, color: 'error.main' }
      ]
    : [];

  const statusConfig = {
    submitted: { label: getStatusLabel('submitted'), color: getInvoiceStatusColor('submitted') },
    approved: { label: getStatusLabel('approved'), color: getInvoiceStatusColor('approved') },
    rejected: { label: getStatusLabel('rejected'), color: getInvoiceStatusColor('rejected') },
    paid: { label: getInvoiceStatusLabel('paid'), color: getInvoiceStatusColor('paid') }
  };

  const columns = [
    {
      id: 'invoice_number',
      label: 'Invoice #',
      render: (_value, row) => row.custom_invoice_number || row.invoice_number,
      primary: true
    },
    {
      id: 'vendor',
      label: 'Vendor',
      accessor: 'vendor.company_name'
    },
    {
      id: 'category',
      label: 'Category',
      render: (_value, row) => <Typography variant="body2">{categories[row.opex_category] || row.opex_category}</Typography>
    },
    {
      id: 'invoice_date',
      label: 'Date',
      accessor: 'invoice_date',
      type: 'date'
    },
    {
      id: 'amount',
      label: 'Amount',
      accessor: 'amount',
      type: 'currency',
      align: 'right',
      currencyAccessor: 'currency'
    },
    {
      id: 'status',
      label: 'Status',
      accessor: 'status',
      type: 'status',
      statusConfig
    }
  ];

  return (
    <Box>
      {/* Statistics Cards */}
      <StatisticsCards statistics={statisticsData} loading={!statistics} columns={5} />

      {/* Main Content */}
      <MainCard>
        <TableFilters
          search={search}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search invoices..."
          filters={[
            {
              label: 'Status',
              value: statusFilter,
              onChange: handleStatusFilterChange,
              options: [
                { value: 'all', label: 'All Statuses' },
                { value: 'submitted', label: 'Submitted' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'paid', label: 'Remittance Advice' }
              ]
            },
            {
              label: 'Category',
              value: categoryFilter,
              onChange: handleCategoryFilterChange,
              options: [
                { value: 'all', label: 'All Categories' },
                ...Object.entries(categories).map(([key, label]) => ({
                  value: key,
                  label: label
                }))
              ]
            }
          ]}
        />

        <DataTable
          columns={columns}
          data={filteredInvoices}
          loading={loading}
          emptyMessage={invoices.length === 0 ? 'No OPEX invoices found' : 'No invoices match the current filters'}
          pagination={{ enabled: false }}
          actions={{
            onView: (row) => navigate(`/admin/opex-invoices/${row.id}`),
            onPay: {
              condition: (row) => row.status === 'approved',
              handler: (row) => setConfirmDialog({ open: true, invoiceId: row.id })
            }
          }}
        />
      </MainCard>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Mark Invoice as Paid"
        message="Are you sure you want to mark this invoice as paid?"
        confirmText="Mark as Paid"
        confirmColor="success"
        onConfirm={handleMarkAsPaid}
        onCancel={() => setConfirmDialog({ open: false, invoiceId: null })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OpexInvoiceList;
